import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronLeft, Download, Repeat, RotateCcw, Star, MapPin, RefreshCcw } from "lucide-react";
import { api, apiErr, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useRealtime } from "@/context/RealtimeContext";
import OrderStatusPill from "@/components/orders/OrderStatusPill";
import PaymentBadge from "@/components/orders/PaymentBadge";
import ReturnModal from "@/components/orders/ReturnModal";
import ReviewModal from "@/components/orders/ReviewModal";
import { smartReorder } from "@/lib/reorder";

const BRAND = "#d81f26";
const FLOW = ["Pending", "Confirmed", "Processing", "Packed", "Ready", "Out for delivery", "Delivered"];
const RET_STEP = { requested: "Return Requested", approved: "Approved", pickup_scheduled: "Pickup Scheduled", picked_up: "Picked Up", received: "Received at store", refund_initiated: "Refund Initiated", refund_completed: "Refund Complete", refunded: "Refund Complete", replacement_initiated: "Replacement Sent", replacement_completed: "Replacement Delivered", closed: "Closed", rejected: "Rejected" };

export default function OrderTracking() {
  const { id } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const cart = useCart();
  const { subscribe } = useRealtime();
  const [o, setO] = useState(loc.state?.order || null);
  const [retModal, setRetModal] = useState(false);
  const [revModal, setRevModal] = useState(false);
  const [rets, setRets] = useState([]);

  const load = useCallback(() => {
    api.get(`/orders/${id}`).then((r) => setO(r.data)).catch(() => { if (!loc.state?.order) setO(false); });
    api.get(`/orders/${id}/returns`).then((r) => setRets(r.data)).catch(() => setRets([]));
  }, [id, loc.state]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => subscribe((m) => { if (m.payload?.order_id === id) load(); }), [subscribe, id, load]);

  if (o === false) return <div className="max-w-2xl mx-auto p-10 text-slate-400">Order nahi mila.</div>;
  if (!o) return <div className="max-w-2xl mx-auto p-10 text-slate-400">Loading…</div>;

  const cancelled = ["Cancelled", "Returned", "Refunded"].includes(o.status);
  const curIdx = FLOW.indexOf(o.status);
  const canCancel = ["Pending", "Confirmed", "Processing"].includes(o.status);

  const cancel = async () => { try { await api.post(`/orders/${id}/cancel`); toast.success("Order cancel ho gaya"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const reorder = () => smartReorder(o, cart);

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <button onClick={() => nav("/orders")} className="flex items-center gap-1 text-slate-500 text-sm font-medium mb-4"><ChevronLeft size={16} /> My Orders</button>

      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h1 className="text-xl font-extrabold" data-testid="tracking-order-no">#{o.order_no}</h1>
            <p className="text-xs text-slate-500">{new Date(o.created_at).toLocaleString("en-IN")}</p>
          </div>
          <OrderStatusPill status={o.status} testid="tracking-status" />
        </div>
        <div className="mt-2"><PaymentBadge method={o.payment_method} status={o.payment_status} testid="tracking-pay" /></div>

        {!cancelled ? (
          <div className="mt-6" data-testid="tracking-timeline">
            {FLOW.map((s, i) => {
              const done = i <= curIdx;
              const active = i === curIdx;
              return (
                <div key={s} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center" style={done ? { backgroundColor: BRAND, color: "#fff" } : { backgroundColor: "#f1f5f9", color: "#94a3b8" }}>
                      {done ? <Check size={15} /> : <span className="w-2 h-2 rounded-full bg-current" />}
                    </div>
                    {i < FLOW.length - 1 && <div className="w-0.5 h-8" style={{ backgroundColor: i < curIdx ? BRAND : "#e2e8f0" }} />}
                  </div>
                  <div className={`pb-6 ${done ? "" : "opacity-50"}`}>
                    <p className="font-bold text-sm" style={active ? { color: BRAND } : {}}>{s}</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="mt-4 p-4 rounded-xl text-sm font-semibold" style={{ backgroundColor: "#DC262612", color: "#991b1b" }}>Ye order {o.status} hai.</div>
        )}

        {o.address && (
          <div className="mt-2 mb-4 flex gap-2 text-sm text-slate-600 bg-slate-50 rounded-xl p-3" data-testid="tracking-address">
            <MapPin size={16} className="shrink-0 mt-0.5 text-slate-400" />
            <span>{[o.address.name, o.address.line1, o.address.line2, o.address.city, o.address.pincode].filter(Boolean).join(", ")}{o.address.phone ? ` · ${o.address.phone}` : ""}</span>
          </div>
        )}

        <div className="border-t border-slate-100 pt-4 space-y-2">
          {o.items.map((it) => (
            <div key={it.product_id} className="flex items-center gap-2">
              {it.image ? <img src={it.image} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0" /> : <div className="w-9 h-9 rounded-lg bg-slate-100 shrink-0" />}
              <span className="flex-1 min-w-0 truncate text-sm text-slate-600">{it.name} × {it.quantity}</span>
              <span className="font-semibold text-sm shrink-0">{inr(it.line_total)}</span>
            </div>
          ))}
          <div className="flex justify-between text-sm pt-2 border-t border-slate-100"><span className="text-slate-500">GST</span><span>{inr(o.gst)}</span></div>
          {o.discount > 0 && <div className="flex justify-between text-sm" style={{ color: BRAND }}><span>Discount</span><span>− {inr(o.discount)}</span></div>}
          <div className="flex justify-between text-sm"><span className="text-slate-500">Delivery</span><span>{o.delivery_fee ? inr(o.delivery_fee) : "FREE"}</span></div>
          <div className="flex justify-between font-extrabold text-lg pt-1"><span>Total</span><span>{inr(o.total)}</span></div>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <button data-testid="tracking-invoice" onClick={() => window.open(`/invoice/${o.id}`, "_blank")} className="flex items-center justify-center gap-1.5 h-11 rounded-xl border-[1.5px] font-bold text-sm" style={{ borderColor: BRAND, color: BRAND }}><Download size={16} /> Invoice</button>
          <button data-testid="tracking-reorder" onClick={reorder} className="flex items-center justify-center gap-1.5 h-11 rounded-xl border-[1.5px] font-bold text-sm" style={{ borderColor: BRAND, color: BRAND }}><Repeat size={16} /> Reorder</button>
        </div>

        {o.status === "Delivered" && (
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button data-testid="tracking-return" onClick={() => setRetModal(true)} className="flex items-center justify-center gap-1.5 h-11 rounded-xl border-[1.5px] font-bold text-sm" style={{ borderColor: BRAND, color: BRAND }}><RotateCcw size={16} /> Return</button>
            <button data-testid="tracking-review" onClick={() => setRevModal(true)} className="flex items-center justify-center gap-1.5 h-11 rounded-xl border-[1.5px] border-amber-300 text-amber-600 font-bold text-sm"><Star size={16} /> Review</button>
          </div>
        )}

        {canCancel && (
          <button data-testid="cancel-order-btn" onClick={cancel} className="mt-3 w-full h-11 border border-red-200 text-red-600 rounded-xl font-bold text-sm hover:bg-red-50 transition-colors">Cancel Order</button>
        )}
      </div>

      {rets.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm mt-4" data-testid="tracking-return-timeline">
          <div className="flex items-center gap-2 mb-3">
            <RefreshCcw size={16} color={BRAND} />
            <h2 className="font-extrabold">Return / Refund</h2>
            <span className="ml-auto text-sm font-bold" style={{ color: BRAND }}>{inr(rets[0].refund_amount || 0)}</span>
          </div>
          {(rets[0].timeline || []).map((t, i) => {
            const last = i === (rets[0].timeline.length - 1);
            const rej = t.status === "rejected";
            const c = rej ? "#DC2626" : "#16A34A";
            return (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span className="w-2.5 h-2.5 rounded-full mt-1.5" style={{ backgroundColor: c }} />
                  {!last && <span className="w-0.5 flex-1 bg-slate-200 my-1" />}
                </div>
                <div className="pb-3">
                  <p className="text-sm font-bold" style={rej ? { color: "#DC2626" } : {}}>{RET_STEP[t.status] || t.status}</p>
                  <p className="text-[11px] text-slate-400">{t.at ? new Date(t.at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}{t.note ? ` · ${t.note}` : ""}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {retModal && <ReturnModal order={o} onClose={() => setRetModal(false)} onDone={load} />}
      {revModal && <ReviewModal order={o} onClose={() => setRevModal(false)} onDone={load} />}
    </div>
  );
}
